import { DEFAULT_MESHING_METHOD } from "features/dt-plus-setup/lib/configs";
import { eventNames, trackGAEvent } from "functions/googleAnalytics";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { convertBytesToMB } from "utils/general";

import type { RunExecutionParams } from "../../model/types";

import { useRunExecution } from "./useRunExecution";

type RunFormState = {
  executeRaytracing: boolean;
  file: File | null;
  meshingMethod: string;
  sensorOffsetF: number | undefined;
};

const initialFormState = (): RunFormState => ({
  executeRaytracing: false,
  file: null,
  meshingMethod: DEFAULT_MESHING_METHOD,
  sensorOffsetF: undefined,
});

export const useExecution = (maxFileSizeMB?: number | undefined) => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const { cancelRun, isLoading: isRunning, mutateAsync: triggerRun, uploadProgress } = useRunExecution();

  const handleRun = async (params: Omit<RunExecutionParams, "projectId">) => {
    if (!projectId) return;
    trackGAEvent(eventNames.EXECUTION_RUN, { executeRaytracing: params.executeRaytracing });
    await triggerRun({ ...params, projectId });
  };

  const [form, setForm] = useState<RunFormState>(initialFormState);

  const updateForm = (patch: Partial<RunFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const handleFileChange = (next: File | null) => {
    if (next) {
      const sizeMB = convertBytesToMB(next.size);
      if (!sizeMB) {
        toast.error(t("Execution.run.fileSizeInvalid"));
        return;
      }
      if (maxFileSizeMB && sizeMB > maxFileSizeMB) {
        toast.error(t("Execution.run.fileTooLarge", { max: maxFileSizeMB }));
        return;
      }
    }
    updateForm({ file: next });
  };

  const onRunClick = async () => {
    const file = form.file;
    if (!file) {
      toast.info(t("Execution.run.selectFileFirst"));
      return;
    }
    if (isRunning) {
      toast.info(t("Execution.run.alreadyRunning"));
      return;
    }

    try {
      await handleRun({ ...form, file });
      setForm(initialFormState());
    } catch {
      // Upload cancelled or failed — keep form state so the user can retry.
    }
  };

  return {
    handlers: {
      cancelRun,
      handleFileChange,
      onRunClick,
      updateForm,
    },
    states: {
      form,
      isRunning,
      uploadProgress,
    },
  };
};
